import { Check } from 'lucide-react';
import { AgentDot } from '../../../AgentDot';
import { useTranslation } from '../../../../lib/i18n';
import { type BandCardAgentData, type BandCardAppData } from '../../../../lib/band-cards';
import { ConnectorLogo } from '../../connectors/ConnectorLogo';
import { Button, Typography } from '../../ui';
import styles from './BandAgentRows.module.css';

/**
 * A band's team: one row per agent — the dot, the name in the primary ink and what they own beside it in
 * the secondary (Figma 18942:127454, "Team block").
 *
 * Shared by both band cards so the team reads the same before and after the band exists — the offer and
 * the band it turned into are the same people, and two copies of this would let them drift apart.
 *
 * The dot is the shared `AgentDot`, the same concentric artwork the roster, the home map and the phones'
 * band cards draw. At the design's 16px it carries no initial — a letter at that size is a smudge. It
 * keys on the agent id once the band exists; an OFFER has no ids (those agents are not hired yet), so it
 * keys on the name: still stable, still distinct, and it settles onto the real one once the band exists.
 */
export function BandAgentRows({ agents }: { agents: BandCardAgentData[] }) {
  return (
    <ul className={styles.agents}>
      {agents.map((agent) => (
        <li key={agent.id ?? agent.name} className={styles.agent}>
          <AgentDot id={agent.id ?? agent.name} size={16} />
          <Typography variant="p2" component="span" className={styles.name}>
            {agent.name}
          </Typography>
          {agent.title && (
            <Typography variant="p3" component="span" color="secondary" className={styles.title}>
              {agent.title}
            </Typography>
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * One app NAMED beside its logo.
 *
 * A bare logo cluster is unreadable to someone meeting these apps for the first time: the two or three
 * famous marks carry themselves and every other app renders as an anonymous glyph, so the card asks the
 * owner to connect something it never names. Reported in the owner's own words — *"some app icons are
 * not well known, how will i know what apps you offer me to connect if i dont recognize the logo?"*.
 *
 * The chip is the design's outlined `.Badge Sizes` frame: a hairline, radius 8, 28 tall, a bare 12px logo
 * and the name in Small/Semi Bold. `connected` adds the lime check square after the name, which is what
 * lets a connect chain report itself in place rather than in a second card underneath.
 */
export function BandAppChip({
  app,
  connected = false,
  onSelect,
}: {
  app: BandCardAppData;
  connected?: boolean;
  /** Makes the chip a button that runs this one app's own connect flow. Ignored once it is connected. */
  onSelect?: (app: BandCardAppData) => void;
}) {
  const { t } = useTranslation();
  const content = (
    <>
      {/* The plate treatment is off: the design draws the brand mark bare on the chip. An offer's apps are
          none of them connected, and the chip says where an app stands with the check rather than by
          tinting its logo. */}
      <ConnectorLogo
        connector={{ toolkit: app.toolkit, logo: app.logoUrl, connected: true }}
        name={app.name}
        size={12}
        radius={0}
        glyphSize={12}
        tileBackground="transparent"
      />
      <Typography variant="h4" component="span" className={styles.chipName}>
        {app.name}
      </Typography>
      {connected && (
        <span className={styles.chipCheck} aria-hidden>
          <Check size={10} strokeWidth={2.5} absoluteStrokeWidth />
        </span>
      )}
    </>
  );
  const selectable = !connected && onSelect !== undefined;
  return (
    <span
      className={selectable ? styles.selectableChip : styles.chip}
      role="listitem"
      aria-label={connected ? `${app.name}, ${t('common.connected')}` : selectable ? `${t('common.connect')}: ${app.name}` : app.name}
    >
      {selectable ? (
        <Button
          variant="outlined"
          size="small"
          className={styles.chipButton}
          aria-label={`${t('common.connect')}: ${app.name}`}
          onClick={() => onSelect(app)}
        >
          <span className={styles.chipContent}>{content}</span>
        </Button>
      ) : (
        content
      )}
    </span>
  );
}

/**
 * A cluster of named chips, WRAPPING — iOS's `FlowLayout` over `BandAppChip`.
 *
 * It lives here rather than being laid out twice at the two call sites, so the offer panel's apps and the
 * band card's connect checklist cannot end up spaced differently, and neither card can regress to a
 * single truncating row.
 */
export function BandAppChips({
  apps,
  /** Per-app, because the band card's cluster fills in with checks while the offer's apps have none. */
  isConnected,
  onSelectApp,
  ariaLabel,
}: {
  apps: BandCardAppData[];
  isConnected?: (app: BandCardAppData) => boolean;
  /** Tapping an unconnected chip opens that app's own connect flow. Absent = chips stay read-only. */
  onSelectApp?: (app: BandCardAppData) => void;
  ariaLabel?: string;
}) {
  return (
    <div className={styles.chips} role="list" aria-label={ariaLabel}>
      {apps.map((app) => (
        <BandAppChip key={app.toolkit} app={app} connected={isConnected?.(app) ?? false} onSelect={onSelectApp} />
      ))}
    </div>
  );
}
